import { ApiError, serverMessage } from '@ghp/api-client'
import { displayName, type UpdateInquiryContacts } from '@ghp/domain'
import { useToast } from '@ghp/ui'
import { useQueryClient } from '@tanstack/react-query'

import { useConnectionStatus } from '../../app/connection'
import type { FieldContext } from '../../app/routes/root'
import { useTeamData } from '../../app/use-team-data'
import type { AuthStore } from '../auth/auth-store'
import { cacheInquiries } from '../../offline/data-db'
import { INQUIRIES_QUERY_KEY } from '../../offline/query-keys'
import type { ContactsEditing } from './components/ContactsCard'

/** Pourquoi « Modifier » est grisé — la seule condition, dite en une phrase. */
const OFFLINE_REASON =
  'La correction part directement au bureau : elle attend que la tablette soit en ligne.'

/**
 * La correction des coordonnées d'une demande, **en ligne seulement** (`PATCH
 * /inquiries/:id/contacts`, seconde exception à D4).
 *
 * ⚠️ **La connexion vient de `useConnectionStatus`, jamais de `navigator.onLine`** : une tablette
 * qui a du réseau et une session que le serveur n'a pas confirmée n'est pas en ligne pour lui.
 *
 * ⚠️ **La réponse remplace la ligne en cache, avec son `servedAt` d'origine** : c'est lui que
 * `returnedToField` compare à l'envoi, et le rafraîchir ferait passer ce contrôle pour renvoyé
 * par le bureau.
 */
export function useContactsCorrection(
  {
    auth,
    contacts,
    accessToken: currentAccessToken,
  }: Pick<FieldContext, 'contacts' | 'accessToken'> & { auth: AuthStore },
  inquiryId: string,
): ContactsEditing {
  const data = useTeamData()
  const queryClient = useQueryClient()
  const toast = useToast()
  const connection = useConnectionStatus(auth)

  return {
    unavailable: connection === 'online' ? null : OFFLINE_REASON,
    save: async (request: UpdateInquiryContacts) => {
      const accessToken = currentAccessToken()
      if (accessToken === null) throw new Error(OFFLINE_REASON)

      let row
      try {
        row = await contacts.updateContacts(accessToken, inquiryId, request)
      } catch (failure: unknown) {
        // Le `401` remonte à la session comme partout ailleurs : un jeton mort doit faire
        // basculer la tablette en dégradé, et « Modifier » se grise avec la bonne raison.
        if (failure instanceof ApiError && failure.status === 401) auth.tokenRefused(accessToken)
        // La carte affiche le message tel quel : lui donner la phrase du serveur, jamais la ligne
        // technique de l'erreur HTTP.
        throw new Error(serverMessage(failure) ?? undefined, { cause: failure })
      }

      const cached = await data.inquiries.get(inquiryId)
      await cacheInquiries(data, [row], cached?.servedAt)
      await queryClient.invalidateQueries({ queryKey: INQUIRIES_QUERY_KEY })

      const who = row.contact ?? row.owner
      // Une personne sans nom (un téléphone seul, #268) ne se nomme pas dans le toast.
      const name = who === null ? null : displayName(who)
      toast(
        'Coordonnées enregistrées',
        name === null ? 'Envoyé au bureau' : `${name} · envoyé au bureau`,
      )
    },
  }
}
